/**
 * InputForm component for debt calculation inputs
 * Constitutional requirement: React with regular JavaScript, console logging
 */

import React, { useState, useEffect } from 'react'
import { 
  Trash2, 
  CreditCard, 
  BarChart3, 
  Clock, 
  DollarSign, 
  Calculator,
  Target, 
  Lightbulb 
} from 'lucide-react'
import { validateNumericField, validateDebtCalculation } from '../utils/validation.js'

const InputForm = ({ onCalculate, isCalculating = false }) => {
  //console.log('🏗️ Rendering InputForm component, isCalculating:', isCalculating)

  // Form state
  const [formData, setFormData] = useState({
    balance: '',
    interestRate: '',
    monthlyPayment: '',
    targetMonths: '',
    targetYears: '',
    calculationMode: 'payoffTime' // 'payoffTime' or 'monthlyPayment'
  })

  // Validation state
  const [errors, setErrors] = useState({})
  const [touched, setTouched] = useState({})

  // Handle input changes with real-time validation
  const handleInputChange = (field, value) => {
    //console.log(`📝 Input change - ${field}: ${value}`)
    
    // Clear form error when user starts typing
    if (errors.formError) {
      setErrors(prev => {
        const newErrors = { ...prev }
        delete newErrors.formError
        return newErrors
      })
    }
    
    setFormData(prev => ({
      ...prev,
      [field]: value
    }))

    setTouched(prev => ({
      ...prev,
      [field]: true
    }))

    // Real-time validation for numeric fields
    if (['balance', 'interestRate', 'monthlyPayment', 'targetMonths', 'targetYears'].includes(field)) {
      let validationValue = value
      let validationField = field

      // Special handling for timeframe fields - validate combined total
      if (field === 'targetYears' || field === 'targetMonths') {
        // Calculate combined timeframe for validation
        const years = field === 'targetYears' ? (parseFloat(value) || 0) : (parseFloat(formData.targetYears) || 0)
        const months = field === 'targetMonths' ? (parseFloat(value) || 0) : (parseFloat(formData.targetMonths) || 0)
        const totalMonths = (years * 12) + months
        
        // Check if we have any timeframe specified
        const hasTimeframe = totalMonths > 0
        
        if (hasTimeframe) {
          // If we have valid timeframe, clear errors on both fields
          setErrors(prev => ({
            ...prev,
            targetMonths: null,
            targetYears: null
          }))
        } else {
          // If no timeframe, check if either field has been touched
          if (touched.targetMonths || touched.targetYears || field === 'targetMonths' || field === 'targetYears') {
            // Only show error if user has interacted with timeframe fields
            setErrors(prev => ({
              ...prev,
              targetMonths: 'Target months is required',
              targetYears: 'Target months is required'
            }))
          }
        }
        return // Exit early for timeframe fields
      }

      // Standard validation for other fields
      const validation = validateNumericField(validationField, validationValue)
      setErrors(prev => ({
        ...prev,
        [field]: validation.isValid ? null : validation.error
      }))
      
      //console.log(`validation error:  ${validation.error}`)

      if (!validation.isValid) {
        console.log(`❌ Validation error for ${field}:`, validation.error)
      }
    }
  }

  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault()

    console.log('📤 Form submission initiated with data:', formData)
    // Calculate target months for validation - always combine years and months
    let targetMonthsValue = 0
    if (formData.calculationMode === 'monthlyPayment') {
      const years = parseFloat(formData.targetYears) || 0
      const months = parseFloat(formData.targetMonths) || 0
      targetMonthsValue = (years * 12) + months
      console.log(`🧮 Combined timeframe: ${years} years + ${months} months = ${targetMonthsValue} total months`)
    }

    // Mark all fields as touched based on calculation mode
    const touchedFields = {
      balance: true,
      interestRate: true,
      calculationMode: true
    }

    if (formData.calculationMode === 'payoffTime') {
      touchedFields.monthlyPayment = true
    } else {
      touchedFields.targetMonths = true
      touchedFields.targetYears = true
      
      // Validate that at least some timeframe is specified and within range
      if (targetMonthsValue < 1) {
        setErrors(prev => ({
          ...prev,
          formError: 'Please fix the following errors before calculating:',
          targetMonths: 'Please specify at least 1 month total timeframe',
          targetYears: 'Please specify at least 1 month total timeframe'
        }))
        setTouched(touchedFields)
        return
      }
      
      if (targetMonthsValue > 1200) {
        setErrors(prev => ({
          ...prev,
          formError: 'Please fix the following errors before calculating:',
          targetMonths: 'Maximum timeframe is 100 years (1200 months)',
          targetYears: 'Maximum timeframe is 100 years (1200 months)'
        }))
        setTouched(touchedFields)
        return
      }
    }

    setTouched(touchedFields)

    // Validate all fields based on calculation mode
    // Don't default empty values to 0 - pass the original values for proper validation
    const numericData = {
      balance: formData.balance === '' ? null : parseFloat(formData.balance),
      interestRate: formData.interestRate === '' ? null : parseFloat(formData.interestRate),
      calculationMode: formData.calculationMode
    }

    if (formData.calculationMode === 'payoffTime') {
      numericData.monthlyPayment = formData.monthlyPayment === '' ? null : parseFloat(formData.monthlyPayment)
    } else {
      numericData.targetMonths = targetMonthsValue
    }

    console.log('🔍 Validating form data:', numericData)
    const validation = validateDebtCalculation(numericData, formData.calculationMode)
    
    if (validation.isValid) {
      console.log('✅ Form validation passed, triggering calculation')
      onCalculate(numericData)
    } else {
      console.log('❌ Form validation failed:', validation.errors)
      
      // Set the validation errors directly from the validation object
      // The validation function returns errors keyed by field name
      const fieldErrors = { ...validation.errors }
      
      // Add a general form error message using the formError field
      fieldErrors.formError = 'Please fix the following errors before calculating:'
      
      setErrors(fieldErrors)
    }
  }

  // Clear form
  const handleClear = () => {
    console.log('🗑️ Clearing form data')
    setFormData({
      balance: '',
      interestRate: '',
      monthlyPayment: '',
      targetMonths: '',
      targetYears: '',
      calculationMode: formData.calculationMode // retain current mode
    })
    setErrors({}) // This will clear all errors including formError
    setTouched({})
  }

  //console.log('🎨 Rendering InputForm with form data:', formData)

  return (
    <div className="input-form">
      <h2>
        <CreditCard size={20} color="#ff6b35" style={{display: 'inline', marginRight: '8px'}} />
        Debt Calculator
      </h2>
      
      <form onSubmit={handleSubmit} className="calculation-form">
        
        {/* Form-level error message */}
        {errors.formError && (
          <div className="error-message" >
            {errors.formError}
          </div>
        )}
        
        {/* Calculation Mode Toggle */}
        <div className="form-group calculation-mode-group">
          <label className="calculation-mode-label">
            <BarChart3 size={16} color="#ff6b35" style={{display: 'inline', marginRight: '4px'}} />
            Calculation Mode
          </label>
          <div className="calculation-mode-toggle">
            <div 
              className={`toggle-option ${formData.calculationMode === 'payoffTime' ? 'active' : ''}`}
              onClick={() => handleInputChange('calculationMode', 'payoffTime')}
            >
              <span className="toggle-icon">
                <Clock 
                  size={16} 
                  color={formData.calculationMode === 'payoffTime' ? '#ffffff' : '#ff6b35'} 
                  style={{display: 'inline', marginRight: '4px'}}
                />
              </span>
              <span className="toggle-text">How long to pay off?</span>
            </div>
            <div 
              className={`toggle-option ${formData.calculationMode === 'monthlyPayment' ? 'active' : ''}`}
              onClick={() => handleInputChange('calculationMode', 'monthlyPayment')}
            >
              <span className="toggle-icon">
                <CreditCard 
                  size={16} 
                  color={formData.calculationMode === 'monthlyPayment' ? '#ffffff' : '#ff6b35'} 
                  style={{display: 'inline', marginRight: '4px'}}
                />
              </span>
              <span className="toggle-text">How much to pay monthly?</span>
            </div>
          </div>
        </div>
        
        {/* Credit Card Balance */}
        <div className="form-group">
          <label htmlFor="balance">
            <DollarSign size={16} color="#ff6b35" style={{display: 'inline', marginRight: '4px'}} />
            Credit Card Balance
            <span className="required">*</span>
          </label>
          <div className="input-wrapper">
            <span className="currency-symbol">$</span>
            <input
              type="number"
              id="balance"
              value={formData.balance}
              onChange={(e) => handleInputChange('balance', e.target.value)}
              placeholder="Enter current balance"
              min="1"
              max="1000000"
              step="0.01"
              disabled={isCalculating}
              className={errors.balance && touched.balance ? 'error' : ''}
            />
          </div>
          {errors.balance && touched.balance && (
            <div className="error-message">{errors.balance}</div>
          )}
          <div className="field-hint">Range: $1 - $1,000,000</div>
        </div>

        {/* Interest Rate */}
        <div className="form-group">
          <label htmlFor="interestRate">
            <BarChart3 size={16} color="#ff6b35" style={{display: 'inline', marginRight: '4px'}} />
            Annual Interest Rate (APR)
            <span className="required">*</span>
          </label>
          <div className="input-wrapper">
            <input
              type="number"
              id="interestRate"
              value={formData.interestRate}
              onChange={(e) => handleInputChange('interestRate', e.target.value)}
              placeholder="Enter interest rate"
              min="0"
              max="100"
              step="0.01"
              disabled={isCalculating}
              className={errors.interestRate && touched.interestRate ? 'error' : ''}
            />
            <span className="percent-symbol">%</span>
          </div>
          {errors.interestRate && touched.interestRate && (
            <div className="error-message">{errors.interestRate}</div>
          )}
          <div className="field-hint">Range: 0% - 100%</div>
        </div>

        {/* Conditional Fields Based on Calculation Mode */}
        {formData.calculationMode === 'payoffTime' ? (
          /* Monthly Payment - for payoff time calculation */
          <div className="form-group">
            <label htmlFor="monthlyPayment">
              <CreditCard size={16} color="#ff6b35" style={{display: 'inline', marginRight: '4px'}} />
              Monthly Payment
              <span className="required">*</span>
            </label>
            <div className="input-wrapper">
              <span className="currency-symbol">$</span>
              <input
                type="number"
                id="monthlyPayment"
                value={formData.monthlyPayment}
                onChange={(e) => handleInputChange('monthlyPayment', e.target.value)}
                placeholder="Enter monthly payment"
                min="1"
                max="100000"
                step="0.01"
                disabled={isCalculating}
                className={errors.monthlyPayment && touched.monthlyPayment ? 'error' : ''}
              />
            </div>
            {errors.monthlyPayment && touched.monthlyPayment && (
              <div className="error-message">{errors.monthlyPayment}</div>
            )}
            <div className="field-hint">Range: $1 - $100,000</div>
          </div>
        ) : (
          /* Target Timeframe - for monthly payment calculation */
          <div className="form-group">
            <label htmlFor="targetTime">
              <Clock size={16} color="#ff6b35" style={{display: 'inline', marginRight: '4px'}} />
              Target Payoff Time
              <span className="required">*</span>
            </label>
            <div className="timeframe-inputs">
              <div className="years-months-inputs">
                <div className="time-input-group">
                  <div className="input-wrapper">
                    <input
                      type="number"
                      id="targetYears"
                      value={formData.targetYears}
                      onChange={(e) => handleInputChange('targetYears', e.target.value)}
                      placeholder="0"
                      min="0"
                      max="100"
                      step="1"
                      disabled={isCalculating}
                      className={errors.targetYears && touched.targetYears ? 'error' : ''}
                    />
                    <span className="time-unit-label">years</span>
                  </div>
                </div>
                <div className="time-input-group">
                  <div className="input-wrapper">
                    <input
                      type="number"
                      id="targetMonths"
                      value={formData.targetMonths}
                      onChange={(e) => handleInputChange('targetMonths', e.target.value)}
                      placeholder="0"
                      min="0"
                      max="11"
                      step="1"
                      disabled={isCalculating}
                      className={errors.targetMonths && touched.targetMonths ? 'error' : ''}
                    />
                    <span className="time-unit-label">months</span>
                  </div>
                </div>
              </div>
            </div>
            {((errors.targetMonths && touched.targetMonths) || (errors.targetYears && touched.targetYears)) && (
              <div className="error-message">{errors.targetMonths || errors.targetYears}</div>
            )}
            <div className="field-hint">
              Enter both years (0-100) and months (0-11). Example: 2 years, 8 months = 32 months total
            </div>
          </div>
        )}

        {/* Form Actions */}
        <div className="form-actions">
          <button
            type="submit"
            disabled={isCalculating}
            className="calculate-button primary"
          >
            {isCalculating ? (
              <>
                <Target size={16} color="#fff" style={{display: 'inline', marginRight: '4px'}} />
                Calculating...
              </>
            ) : formData.calculationMode === 'payoffTime' ? (
              <>
                <Calculator size={22} color="#fff" style={{display: 'inline', marginRight: '4px'}} />
                Calculate
              </>
            ) : (
              <>
                <CreditCard size={16} color="#fff" style={{display: 'inline', marginRight: '4px'}} />
                Calculate
              </>
            )}
          </button>
          
          <button
            type="button"
            onClick={handleClear}
            disabled={isCalculating}
            className="clear-button secondary"
          >
            <Trash2 size={16} color="#ff6b35" />
            Clear Form
          </button>
        </div>

      </form>

      {/* Form Tips */}
      <div className="form-tips">
        <h4>
          <Lightbulb size={16} color="#ff6b35" style={{display: 'inline', marginRight: '4px'}} />
          Tips:
        </h4>
        <ul>
          <li><strong>Balance:</strong> Enter your current credit card balance</li>
          <li><strong>Interest Rate:</strong> Find this on your credit card statement (APR)</li>
          {formData.calculationMode === 'payoffTime' ? (
            <li><strong>Monthly Payment:</strong> Amount you plan to pay each month</li>
          ) : (
            <li><strong>Target Time:</strong> How quickly you want to pay off the debt</li>
          )}
          <li><strong>Method:</strong> Uses daily interest compounding (credit card standard)</li>
        </ul>
      </div>
    </div>
  )
}

export default InputForm
